import React from 'react';
import { useParams } from 'react-router-dom';

import { Tooltip } from '@mui/material';
import { icons } from '@theme';
import { Header, Tabs } from '../CommonComponents';
import { Container } from '../StyledComponents';
import UserDetails from './UserDetails';
import UserWatchlists from './UserWatchlists';
import FetchRedux from '../CommonComponents/Fetch/FetchRedux';
import { FetchPropsType } from '../CommonComponents/Fetch/FetchPropsType';
import {
    fetchUserMetaById,
    selectFetchingUserMetaById,
    selectUserMetaById
} from '../../redux/actions/users';
import UserRequests from './UserRequests';
import { carrierNameCapitalized, nonCarrierNameCapitalized } from '../../env';

type UserMetaType = {
    [key: string]: number | string
};

const carrierIcon = ({
    icon: (
        <Tooltip title={carrierNameCapitalized}>
            <icons.carrier color="primary" sx={{ height: 24, width: 24 }} />
        </Tooltip>
    ),
    top: '6px'
});

const buyerIcon = ({
    icon: (
        <Tooltip title={nonCarrierNameCapitalized}>
            <icons.buyer color="primary" sx={{ height: 22, width: 22 }} />
        </Tooltip>
    ),
    top: '4px'
});

function UserTabs(props: FetchPropsType<any>) {
    const { entity } = props;

    const isCarrierFalse = entity?.isCarrier === false || entity?.isCarrier === 'false';

    const tabs = React.useMemo(() => {
        const carrierTabs = [{
            key: 'user',
            label: 'Uppgifter',
            Component: UserDetails,
            padding: 2
        }, {
            key: 'watchlists',
            label: 'Bevakningar',
            Component: UserWatchlists,
        }, {
            key: 'requests',
            label: 'Aktiviteter',
            Component: UserRequests
        }];

        const buyerTabs = carrierTabs.filter((tab) => tab.key !== 'watchlists');

        return !isCarrierFalse ? carrierTabs : buyerTabs;
    }, [isCarrierFalse]);

    const getTitle = React.useCallback((tab) => {
        if (!entity) {
            return tab.label;
        }
        const count = entity[`${tab.key}Count` as string];
        return typeof count === 'number'
            ? `${tab.label} (${count})`
            : tab.label;
    }, [entity]);

    return (
        <Container>
            <Header
                title={entity.name as string ?? 'Användare'}
                subtitle={entity.tenantName}
                subtitleIcon={entity.isCarrier ? carrierIcon : buyerIcon}
            />
            <Tabs
                tabsKey="users"
                tabs={tabs}
                getTitle={getTitle}
            />
        </Container>
    );
}

export default function UserTabsFetched() {
    const { id } = useParams() as unknown as { id: number };

    return (
        <FetchRedux<UserMetaType>
            fetchEntity={fetchUserMetaById(id)}
            selectEntity={selectUserMetaById(id)}
            selectIsFetching={selectFetchingUserMetaById(id)}
            Component={UserTabs}
            name="användaren"
        />
    );
}
